import { z } from "zod";
import { cn } from "@/shared/lib/utils";
import { StatCard, StatCardSkeleton } from "@/shared/components";
import { defineWidget } from "./types";
import type { AgentWidgetProps } from "./types";
import { registerWidget } from "./widget-registry";
import { WIDGET_TYPES } from "@modootoday/core-agent/constants";

/* ────────────────────── Schema ────────────────────── */

const KpiCardsSchema = z.object({
  items: z.array(
    z.object({
      label: z.string(),
      value: z.string(),
      change: z.string().optional(),
      trend: z.enum(["up", "down", "neutral"]).optional(),
    }),
  ),
});

type KpiCardsData = z.infer<typeof KpiCardsSchema>;

/* ────────────────────── Component ────────────────────── */

export function KpiCards({
  widget,
  onDeepLink,
  className,
}: AgentWidgetProps<KpiCardsData>) {
  const { items } = widget.data;
  const hasLink = Boolean(widget.deepLink && onDeepLink);

  return (
    <div
      className={cn(
        "grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4",
        className,
      )}
    >
      {items.map((item, idx) => (
        <div
          key={idx}
          className={cn(hasLink && "cursor-pointer")}
          onClick={hasLink ? () => onDeepLink!(widget.deepLink!) : undefined}
        >
          <StatCard
            label={item.label}
            value={item.value}
            change={item.change}
            trend={item.trend ?? "neutral"}
            className={cn(
              "h-full",
              hasLink && "transition-shadow hover:shadow-md",
            )}
          />
        </div>
      ))}
    </div>
  );
}

/* ────────────────────── Skeleton ────────────────────── */

export function KpiCardsSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4",
        className,
      )}
    >
      {Array.from({ length: 4 }, (_, i) => (
        <StatCardSkeleton key={i} />
      ))}
    </div>
  );
}

/* ────────────────────── Widget Definition ────────────────────── */

const kpiCardsWidget = defineWidget<KpiCardsData>({
  type: WIDGET_TYPES.KPI_CARDS,
  label: "KPI 카드 그리드",
  schema: KpiCardsSchema,
  component: KpiCards,
  skeleton: KpiCardsSkeleton,
  defaultLayout: "full",
  maxHeight: 120,
  aiHint: "KPI 요약 (키워드 수, 채널 수, 콘텐츠 수, 평균 점수 등)",
});

registerWidget(kpiCardsWidget);
